import { Link } from "react-router-dom";
import { Banknote, Truck } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { LOGO_PNG, LOGO_ASPECT } from "@/lib/logo";

export default function Footer() {
  const { t, lang } = useLang();

  const cols = [
    {
      title: t.footer.shopCol,
      links: [
        { to: "/boutique", label: t.nav.shop, id: "footer-shop" },
        { to: "/boutique?category=robes", label: lang === "fr" ? "Robes" : lang === "ar" ? "فساتين" : "Dresses", id: "footer-robes" },
        { to: "/boutique?category=hauts", label: lang === "fr" ? "Hauts" : lang === "ar" ? "بلوزات" : "Tops", id: "footer-tops" },
        { to: "/boutique", label: t.home.newIn.title, id: "footer-new" },
      ],
    },
    {
      title: t.footer.helpCol,
      links: [
        { to: "/livraison", label: t.ship.title, id: "footer-shipping" },
        { to: "/retours", label: t.returns.title.split(" ")[0], id: "footer-returns" },
        { to: "/guide-des-tailles", label: t.size.title, id: "footer-size" },
        { to: "/faq", label: t.faq.title, id: "footer-faq" },
      ],
    },
    {
      title: t.footer.houseCol,
      links: [
        { to: "/a-propos", label: t.nav.about, id: "footer-about" },
        { to: "/contact", label: t.nav.contact, id: "footer-contact" },
      ],
    },
  ];

  return (
    <footer className="bg-ivory border-t border-ink/10" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 pt-16 pb-10">
        <div className="flex flex-wrap justify-center gap-x-10 sm:gap-x-16 gap-y-4 pb-12 mb-12 border-b border-ink/10" data-testid="footer-reassurance">
          {[
            { icon: Truck, label: t.footer.shipStrip, id: "footer-strip-shipping" },
            { icon: Banknote, label: t.footer.codStrip, id: "footer-strip-cod" },
          ].map(({ icon: Icon, label, id }) => (
            <span key={id} className="flex items-center gap-2.5 text-[12px] text-ink/60" data-testid={id}>
              <Icon className="w-[18px] h-[18px] text-rose-deep" strokeWidth={1.5} />
              {label}
            </span>
          ))}
        </div>

        <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-12">
          <div>
            <img src={LOGO_PNG} alt="MIORA Boutique" style={{ height: 44, aspectRatio: LOGO_ASPECT }} />
            <p className="mt-5 text-sm text-ink/60 max-w-xs leading-relaxed">{t.footer.tagline}</p>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px]" data-testid="footer-socials">
              <a href="https://instagram.com/mioraboutique_" target="_blank" rel="noreferrer" className="text-ink/70 hover:text-rose-deep transition-colors" data-testid="footer-social-instagram">
                Instagram — @mioraboutique_
              </a>
              <a href="https://tiktok.com/@mioraboutique_" target="_blank" rel="noreferrer" className="text-ink/70 hover:text-rose-deep transition-colors" data-testid="footer-social-tiktok">
                TikTok — @mioraboutique_
              </a>
              <a href="https://snapchat.com/add/mioraboutqiue" target="_blank" rel="noreferrer" className="text-ink/70 hover:text-rose-deep transition-colors" data-testid="footer-social-snapchat">
                Snapchat — mioraboutqiue
              </a>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <p className="eyebrow mb-5">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.id}>
                    <Link to={l.to} data-testid={l.id} className="text-sm text-ink/70 hover:text-rose-deep transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-ink/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-[12px] text-ink/50">{t.footer.rights}</p>
          <p className="text-[12px] text-ink/50">{t.footer.paymentNote}</p>
        </div>
      </div>
    </footer>
  );
}
